import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';

/**
 * boneyard dev toggle — DEV ONLY.
 *
 * `?skeleton=1` forces every `<Skeleton loading={useSkel(x)}>` to render its skeleton,
 * persisted to sessionStorage so it survives client-side navigation. `?skeleton=0` clears it.
 * No-op in production: guarded by `import.meta.env.PROD` and SSR-safe.
 */
const ForceSkeletonContext = createContext(false);

export function isBoneyardBuild(): boolean {
  // SAFETY: the CLI sets this non-standard flag before the app boots; it is
  // absent from the standard Window type and never set in normal runs.
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { __BONEYARD_BUILD?: boolean }).__BONEYARD_BUILD === true
  );
}

/**
 * Merge a real `loading` flag with the dev-only override.
 * In production this returns `real` unchanged.
 *
 * During boneyard capture (`window.__BONEYARD_BUILD`) this forces true so that
 * Skeletons gated behind a loading flag still mount and render their fixture
 * for the snapshot — capture runs after `networkidle`, when real data is loaded
 * and `real` would otherwise be false.
 */
export function useSkel(real: boolean): boolean {
  const forced = useContext(ForceSkeletonContext);
  // A real-data capture must snapshot the page exactly as a learner sees it.
  // Forcing `loading` on would make every call site render its loading branch, so
  // the extractor would record those bones instead of the real layout. Inlined
  // rather than imported so this module's export surface - and the seven suites
  // that mock it - stays unchanged.
  if (isRealDataCapture()) return real;
  return real || forced || isBoneyardBuild();
}

/**
 * Capture against the live page instead of the deterministic fixture.
 *
 * `?realbones=1` inside a boneyard build. Not exported: `boneyard-skeleton`
 * keeps its own copy for the fixture slot, and test suites mock this module with
 * an explicit export list.
 */
function isRealDataCapture(): boolean {
  if (typeof window === 'undefined' || !isBoneyardBuild()) return false;
  return new URLSearchParams(window.location.search).has('realbones');
}

/**
 * True when the dev `?skeleton=1` override is active. Use it for capture-only
 * skeletons (gated on `isBoneyardBuild()`) so they also preview in dev - the
 * capture flag is only set by the CLI, the dev toggle only by `?skeleton=1`.
 */
export function useForceSkeleton(): boolean {
  return useContext(ForceSkeletonContext);
}

/**
 * True whenever a skeleton preview is on screen: the dev `?skeleton=1` toggle
 * or a boneyard capture run. Use it to suppress real-content chrome (end-of-list
 * messages, counters) that would otherwise sit under the bones.
 */
export function useSkeletonPreview(): boolean {
  const forced = useContext(ForceSkeletonContext);
  return forced || isBoneyardBuild();
}

export function DevSkeletonProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Two-pass: the first client render must match SSR (force=false) or React
  // throws a hydration mismatch - sessionStorage is only readable after mount,
  // so the flag flips in an effect and skeletons appear one paint later.
  const [force, setForce] = useState(false);
  useEffect(() => {
    // Prod never force-skeletons, even with a stale flag in sessionStorage.
    if (import.meta.env.PROD) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('skeleton') === '1') {
        sessionStorage.setItem('til:force-skeleton', '1');
      } else if (params.get('skeleton') === '0') {
        sessionStorage.removeItem('til:force-skeleton');
      }
      setForce(sessionStorage.getItem('til:force-skeleton') === '1');
    } catch {
      return;
    }
  }, [location.pathname]);

  return <ForceSkeletonContext.Provider value={force}>{children}</ForceSkeletonContext.Provider>;
}
